import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import type { BaseAction } from '../@types/actions';
import * as http from '../Http';
import requestCache from '../utils/request-cache';

import { callbackSearch } from './search';

import type {
  SubscriptionSearch,
  SubscriptionSearchCreate,
  SubscriptionSearchItem,
} from '../@types/AdvertismentSubscription';
import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPostRequest,
  buildAuthorizedPutRequest,
} from '../Http/requestTemplates';
import QueryFilter from '../helpers/query-filter';
import type { RootState } from '../reducers';

export const REQ_SEARCH_SUBSCRIPTION_LIST = 'REQ/SEARCH_SUBSCRIPTION_LIST';
export const RCV_SEARCH_SUBSCRIPTION_LIST = 'RCV/SEARCH_SUBSCRIPTION_LIST';
type REQ_SEARCH_SUBSCRIPTION_LIST_TYPE = BaseAction<typeof REQ_SEARCH_SUBSCRIPTION_LIST>;
type RCV_SEARCH_SUBSCRIPTION_LIST_TYPE = BaseAction<typeof RCV_SEARCH_SUBSCRIPTION_LIST> & {
  response: SubscriptionSearchItem[];
};

export const REQ_SET_SEARCH_SUBSCRIPTION_ITEM = 'REQ/SET_SEARCH_SUBSCRIPTION_ITEM';
type REQ_SET_SEARCH_SUBSCRIPTION_ITEM_TYPE = BaseAction<typeof REQ_SET_SEARCH_SUBSCRIPTION_ITEM> & {
  subscription: SubscriptionSearchItem | undefined;
};

export const REQ_CREATE_SEARCH_SUBSCRIPTION = 'REQ/CREATE_SEARCH_SUBSCRIPTION';
export const RCV_CREATE_SEARCH_SUBSCRIPTION = 'RCV/CREATE_SEARCH_SUBSCRIPTION';
type REQ_CREATE_SEARCH_SUBSCRIPTION_TYPE = BaseAction<typeof REQ_CREATE_SEARCH_SUBSCRIPTION>;
type RCV_CREATE_SEARCH_SUBSCRIPTION_TYPE = BaseAction<typeof RCV_CREATE_SEARCH_SUBSCRIPTION> & {
  response: SubscriptionSearch;
  sub: SubscriptionSearchCreate;
};

export const REQ_UPDATE_SEARCH_SUBSCRIPTION = 'REQ/UPDATE_SEARCH_SUBSCRIPTION';
export const RCV_UPDATE_SEARCH_SUBSCRIPTION = 'RCV/UPDATE_SEARCH_SUBSCRIPTION';
type REQ_UPDATE_SEARCH_SUBSCRIPTION_TYPE = BaseAction<typeof REQ_UPDATE_SEARCH_SUBSCRIPTION>;
type RCV_UPDATE_SEARCH_SUBSCRIPTION_TYPE = BaseAction<typeof RCV_UPDATE_SEARCH_SUBSCRIPTION> & {
  response: SubscriptionSearch;
  sub: SubscriptionSearch;
};

export const REQ_REMOVE_SEARCH_SUBSCRIPTION = 'REQ/REMOVE_SEARCH_SUBSCRIPTION';
export const RCV_REMOVE_SEARCH_SUBSCRIPTION = 'RCV/REMOVE_SEARCH_SUBSCRIPTION';
type REQ_REMOVE_SEARCH_SUBSCRIPTION_TYPE = BaseAction<typeof REQ_REMOVE_SEARCH_SUBSCRIPTION>;
type RCV_REMOVE_SEARCH_SUBSCRIPTION_TYPE = BaseAction<typeof RCV_REMOVE_SEARCH_SUBSCRIPTION> & {
  response: SubscriptionSearch;
};

export const REQUEST_FAILED = 'SEARCH_SUBSCRIPTION/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action:
    | typeof REQ_SEARCH_SUBSCRIPTION_LIST
    | typeof REQ_CREATE_SEARCH_SUBSCRIPTION
    | typeof REQ_UPDATE_SEARCH_SUBSCRIPTION
    | typeof REQ_REMOVE_SEARCH_SUBSCRIPTION;
};

export type SEARCH_SUBSCRIPTION_ACTION =
  | REQ_SEARCH_SUBSCRIPTION_LIST_TYPE
  | RCV_SEARCH_SUBSCRIPTION_LIST_TYPE
  | REQ_SET_SEARCH_SUBSCRIPTION_ITEM_TYPE
  | REQ_CREATE_SEARCH_SUBSCRIPTION_TYPE
  | RCV_CREATE_SEARCH_SUBSCRIPTION_TYPE
  | REQ_UPDATE_SEARCH_SUBSCRIPTION_TYPE
  | RCV_UPDATE_SEARCH_SUBSCRIPTION_TYPE
  | REQ_REMOVE_SEARCH_SUBSCRIPTION_TYPE
  | RCV_REMOVE_SEARCH_SUBSCRIPTION_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<SEARCH_SUBSCRIPTION_ACTION>;

export const set = (subscription: SubscriptionSearchItem) => (dispatch: Dispatch) =>
  dispatch<SEARCH_SUBSCRIPTION_ACTION>({ type: REQ_SET_SEARCH_SUBSCRIPTION_ITEM, subscription });

export const allAndCollectSubscriptions =
  () => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_SEARCH_SUBSCRIPTION_LIST)) {
      return;
    }
    requestCache.isPending(REQ_SEARCH_SUBSCRIPTION_LIST, true);

    dispatch<SEARCH_SUBSCRIPTION_ACTION>({ type: REQ_SEARCH_SUBSCRIPTION_LIST });

    try {
      const response = await http.json<SubscriptionSearchItem[]>(
        '/api/subscriptions/search',
        buildAuthorizedGetRequest(auth.token),
      );

      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: RCV_SEARCH_SUBSCRIPTION_LIST,
        response: response.response,
      });
      if (response.response) {
        response.response.forEach((sub) => {
          try {
            const queryFilter = new QueryFilter(sub.query).add(
              'itemPublished.from',
              sub.lastNotifiedAt,
            );
            callbackSearch(queryFilter, (_resultSet, numberOfHits) => {
              callbackSearch(new QueryFilter(sub.query), (rS, totalHits) => {
                const match = rS.find((x) => !!x.featuredImage);

                const image =
                  match?.featuredImage?.type === 'Image'
                    ? match.featuredImage.files?.[0].formats.find((x) => x.format === '800x')
                        ?.url || null
                    : null;
                dispatch({
                  type: REQ_SET_SEARCH_SUBSCRIPTION_ITEM,
                  subscription: {
                    ...sub,
                    resultSet: rS,
                    numberOfHits,
                    totalHits,
                    image,
                  },
                });
              });
            });
          } catch (_ee) {}
        });
      }
    } catch (_e) {
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: REQUEST_FAILED,
        action: REQ_SEARCH_SUBSCRIPTION_LIST,
      });
    } finally {
      requestCache.isPending(REQ_SEARCH_SUBSCRIPTION_LIST, false);
    }
  };

export const collectSubscription = (subscription: SubscriptionSearch) => (dispatch: Dispatch) => {
  const queryFilter = new QueryFilter(subscription.query).add(
    'itemPublished.from',
    subscription.lastNotifiedAt,
  );

  callbackSearch(queryFilter, (_resultSet, numberOfHits) => {
    callbackSearch(new QueryFilter(subscription.query), (rS, totalHits) => {
      const match = rS.find((x) => !!x.featuredImage);
      const image =
        match?.featuredImage?.type === 'Image'
          ? match.featuredImage.files?.[0].formats.find((x) => x.format === '800x')?.url || null
          : null;

      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: REQ_SET_SEARCH_SUBSCRIPTION_ITEM,
        subscription: {
          ...subscription,
          resultSet: rS,
          numberOfHits,
          totalHits,
          image,
        },
      });
    });
  });
};

export const all = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { auth } = getState();
  if (!auth.token || requestCache.isPending(REQ_SEARCH_SUBSCRIPTION_LIST)) {
    return;
  }
  requestCache.isPending(REQ_SEARCH_SUBSCRIPTION_LIST, true);

  try {
    dispatch<SEARCH_SUBSCRIPTION_ACTION>({ type: REQ_SEARCH_SUBSCRIPTION_LIST });

    const response = await http.json<SubscriptionSearchItem[]>(
      '/api/subscriptions/search',
      buildAuthorizedGetRequest(auth.token),
    );
    http.validateResponse(response);
    dispatch<SEARCH_SUBSCRIPTION_ACTION>({
      type: RCV_SEARCH_SUBSCRIPTION_LIST,
      response: response.response,
    });
  } catch (_e) {
    dispatch<SEARCH_SUBSCRIPTION_ACTION>({
      type: REQUEST_FAILED,
      action: REQ_SEARCH_SUBSCRIPTION_LIST,
    });
  }
  requestCache.isPending(REQ_SEARCH_SUBSCRIPTION_LIST, false);
};

export const create =
  (subscription: SubscriptionSearchCreate) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_CREATE_SEARCH_SUBSCRIPTION)) {
      return;
    }
    requestCache.isPending(REQ_CREATE_SEARCH_SUBSCRIPTION, true);

    try {
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({ type: REQ_CREATE_SEARCH_SUBSCRIPTION });

      const response = await http.json<SubscriptionSearch>(
        '/api/subscriptions/search',
        buildAuthorizedPostRequest(auth.token, { content: subscription }),
      );
      http.validateResponse(response);
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: RCV_CREATE_SEARCH_SUBSCRIPTION,
        response: response.response,
        sub: subscription,
      });
    } catch (_e) {
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: REQUEST_FAILED,
        action: REQ_CREATE_SEARCH_SUBSCRIPTION,
      });
    }
    requestCache.isPending(REQ_CREATE_SEARCH_SUBSCRIPTION, false);
  };

export const update =
  (subscription: SubscriptionSearch | SubscriptionSearchItem) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_UPDATE_SEARCH_SUBSCRIPTION)) {
      return;
    }
    requestCache.isPending(REQ_UPDATE_SEARCH_SUBSCRIPTION, true);

    try {
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({ type: REQ_UPDATE_SEARCH_SUBSCRIPTION });

      const response = await http.json<SubscriptionSearch>(
        `/api/subscriptions/search/${subscription.id}`,
        buildAuthorizedPutRequest(auth.token, { content: subscription }),
      );
      http.validateResponse(response);
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: RCV_UPDATE_SEARCH_SUBSCRIPTION,
        response: response.response,
        sub: subscription,
      });
    } catch (_e) {
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: REQUEST_FAILED,
        action: REQ_UPDATE_SEARCH_SUBSCRIPTION,
      });
    }
    requestCache.isPending(REQ_UPDATE_SEARCH_SUBSCRIPTION, false);
  };

export const remove =
  (subscription: SubscriptionSearch) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_REMOVE_SEARCH_SUBSCRIPTION)) {
      return;
    }
    requestCache.isPending(REQ_REMOVE_SEARCH_SUBSCRIPTION, true);

    try {
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({ type: REQ_REMOVE_SEARCH_SUBSCRIPTION });

      const response = await http.json<SubscriptionSearch>(
        `/api/subscriptions/search/${subscription.id}`,
        buildAuthorizedDeleteRequest(auth.token),
      );
      http.validateResponse(response);
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: RCV_REMOVE_SEARCH_SUBSCRIPTION,
        response: response.response,
      });
    } catch (_e) {
      dispatch<SEARCH_SUBSCRIPTION_ACTION>({
        type: REQUEST_FAILED,
        action: REQ_REMOVE_SEARCH_SUBSCRIPTION,
      });
    }
    requestCache.isPending(REQ_REMOVE_SEARCH_SUBSCRIPTION, false);
  };
